/**
 * useFunnelDropdown
 * 
 * Hook responsável pela lógica de estado e posicionamento do dropdown de funil.
 * Segue SRP isolando a lógica de UI do componente visual.
 * 
 * Princípios aplicados:
 * - Single Responsibility: apenas gerencia estado do dropdown
 * - Separation of Concerns: lógica separada da UI
 */

import { useState, useEffect, useRef, useCallback } from 'react';

interface UseFunnelDropdownReturn {
    isOpen: boolean;
    position: { top: number; left: number };
    dropdownRef: React.RefObject<HTMLDivElement>;
    buttonRef: React.RefObject<HTMLButtonElement>;
    toggle: () => void;
    close: () => void;
}

export const useFunnelDropdown = (): UseFunnelDropdownReturn => {
    const [isOpen, setIsOpen] = useState(false);
    const [position, setPosition] = useState({ top: 0, left: 0 });
    const dropdownRef = useRef<HTMLDivElement>(null);
    const buttonRef = useRef<HTMLButtonElement>(null);

    // fixed + coords de viewport — seguro no shell com scroll interno (mobile/PWA)
    const toggle = useCallback(() => {
        if (!isOpen && buttonRef.current) {
            const rect = buttonRef.current.getBoundingClientRect();
            const panelWidth = Math.min(240, window.innerWidth - 32);
            let left = rect.left;
            if (left + panelWidth > window.innerWidth - 16) {
                left = Math.max(16, rect.right - panelWidth);
            }
            setPosition({
                top: rect.bottom + 4,
                left,
            });
        }
        setIsOpen(prev => !prev);
    }, [isOpen]);

    const close = useCallback(() => {
        setIsOpen(false);
    }, []);

    // Click outside handler
    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (
                dropdownRef.current &&
                !dropdownRef.current.contains(event.target as Node) &&
                buttonRef.current &&
                !buttonRef.current.contains(event.target as Node)
            ) {
                setIsOpen(false);
            }
        };

        if (isOpen) {
            document.addEventListener('mousedown', handleClickOutside);
        }
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, [isOpen]);

    return {
        isOpen,
        position,
        dropdownRef,
        buttonRef,
        toggle,
        close
    };
};
